import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Layers,
  TrendingDown,
  Package,
  ShoppingBag,
  Pencil,
  PawPrint,
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { useFeedingGroups } from '../../contexts/FeedingGroupsContext';
import { usePets } from '../../contexts/PetsContext';
import { formatGrams } from '../../lib/format';
import { estimateGroupProjection } from '../../lib/learning';
import { INVENTORY, ROUTES } from '../../config/constants';

/**
 * Vista detallada de proyecciones de stock por grupo (Fase 6).
 *
 * El "for sale" de esta pantalla vs la lista de inventario:
 *  - InventoryPage es el manager day-to-day: cards con dispensers individuales,
 *    botones de rellenar/abrir bolsa, etc.
 *  - InventoryProjectionsPage es el dashboard de decisión: un row compacto
 *    por grupo con stock total + consumo diario + días restantes + badge,
 *    ordenados por urgencia. Pensada para "¿qué tengo que comprar?".
 *
 * Resumen header arriba: cuántos grupos están bajos, cuántos OK.
 */
const InventoryProjectionsPage = () => {
  const navigate = useNavigate();
  const { groups, isLoading: groupsLoading } = useFeedingGroups();
  const { pets } = usePets();

  const petsByGroup = useMemo(() => {
    const map = new Map();
    for (const pet of pets) {
      if (!pet.feedingGroupId) continue;
      const arr = map.get(pet.feedingGroupId) ?? [];
      arr.push(pet);
      map.set(pet.feedingGroupId, arr);
    }
    return map;
  }, [pets]);

  const projections = useMemo(() => {
    const enriched = groups.map((group) => ({
      group,
      groupPets: petsByGroup.get(group.id) ?? [],
      projection: estimateGroupProjection(group, petsByGroup.get(group.id) ?? [], {
        defaultThresholdDays: INVENTORY.DEFAULT_LOW_STOCK_THRESHOLD_DAYS,
      }),
    }));

    return enriched.sort((a, b) => {
      const aOut = a.projection?.isOutOfStock ? 1 : 0;
      const bOut = b.projection?.isOutOfStock ? 1 : 0;
      if (aOut !== bOut) return bOut - aOut;

      const aLow = a.projection?.isLowStock ? 1 : 0;
      const bLow = b.projection?.isLowStock ? 1 : 0;
      if (aLow !== bLow) return bLow - aLow;

      const aDays = a.projection?.daysRemaining ?? Infinity;
      const bDays = b.projection?.daysRemaining ?? Infinity;
      if (aDays !== bDays) return aDays - bDays;

      return a.group.name.localeCompare(b.group.name);
    });
  }, [groups, petsByGroup]);

  const counts = useMemo(() => {
    let urgent = 0;
    let low = 0;
    let ok = 0;
    let unknown = 0;
    for (const { projection } of projections) {
      if (!projection) continue;
      if (projection.isOutOfStock) urgent++;
      else if (projection.isLowStock) low++;
      else if (projection.daysRemaining != null) ok++;
      else unknown++;
    }
    return { urgent, low, ok, unknown };
  }, [projections]);

  if (groupsLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={ArrowLeft}
          onClick={() => navigate(ROUTES.INVENTORY)}
          className="self-start"
        >
          Volver al inventario
        </Button>
        <Card>
          <p className="text-sm text-slate-500">Cargando proyecciones...</p>
        </Card>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={ArrowLeft}
          onClick={() => navigate(ROUTES.INVENTORY)}
          className="self-start"
        >
          Volver al inventario
        </Button>
        <Card className="flex flex-col items-center gap-4 py-12 text-center">
          <TrendingDown className="h-10 w-10 text-slate-300" aria-hidden="true" />
          <p className="text-sm text-slate-500">
            Todavía no hay grupos. Creá uno desde Inventario para ver proyecciones.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Button
        variant="ghost"
        size="sm"
        leftIcon={ArrowLeft}
        onClick={() => navigate(ROUTES.INVENTORY)}
        className="self-start"
      >
        Volver al inventario
      </Button>

      <div>
        <h1 className="text-2xl font-bold text-slate-900">Proyecciones</h1>
        <p className="text-sm text-slate-500">
          Cuántos días de stock le queda a cada grupo, según el consumo diario combinado.
        </p>
      </div>

      {/* Resumen header */}
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-xl bg-red-50 ring-1 ring-red-100 px-3 py-2.5 text-center">
          <p className="text-2xl font-bold text-status-red leading-none">
            {counts.urgent + counts.low}
          </p>
          <p className="text-[11px] text-red-800 mt-1 leading-tight">
            necesitan reposición
          </p>
        </div>
        <div className="rounded-xl bg-emerald-50 ring-1 ring-emerald-100 px-3 py-2.5 text-center">
          <p className="text-2xl font-bold text-status-green leading-none">{counts.ok}</p>
          <p className="text-[11px] text-emerald-800 mt-1 leading-tight">con buen stock</p>
        </div>
        <div className="rounded-xl bg-slate-50 ring-1 ring-slate-100 px-3 py-2.5 text-center">
          <p className="text-2xl font-bold text-slate-600 leading-none">{counts.unknown}</p>
          <p className="text-[11px] text-slate-600 mt-1 leading-tight">sin mascotas</p>
        </div>
      </div>

      {/* All-good empty state */}
      {counts.urgent + counts.low === 0 && counts.ok > 0 && (
        <div className="flex items-start gap-3 rounded-xl bg-emerald-50 ring-1 ring-emerald-100 px-4 py-3">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-status-green mt-0.5" aria-hidden="true" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-emerald-900">Todo OK.</p>
            <p className="text-xs text-emerald-800/90">
              Ningún grupo está debajo de su umbral configurado.
            </p>
          </div>
        </div>
      )}

      {/* Ranking de grupos */}
      <div className="flex flex-col gap-2">
        {projections.map(({ group, groupPets, projection }) => (
          <ProjectionRow
            key={group.id}
            group={group}
            groupPets={groupPets}
            projection={projection}
          />
        ))}
      </div>
    </div>
  );
};

const ProjectionRow = ({ group, groupPets, projection }) => {
  if (!projection) return null;

  const days = projection.daysRemaining;
  const isUrgent = projection.isOutOfStock;
  const isLow = projection.isLowStock;
  const isOk = days != null && !isUrgent && !isLow;
  const isUnknown = days == null;

  const stateClasses = isUrgent
    ? 'bg-red-50 ring-red-200'
    : isLow
      ? 'bg-amber-50 ring-amber-200'
      : isOk
        ? 'bg-white ring-slate-200'
        : 'bg-slate-50 ring-slate-200';

  return (
    <Card className={`p-0 ring-1 ${stateClasses}`}>
      <div className="p-4">
        <div className="flex items-start gap-3 mb-2">
          <div className="rounded-lg bg-brand-100 p-2 shrink-0">
            <Layers className="h-4 w-4 text-brand-700" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-semibold text-slate-900 truncate">{group.name}</h2>
              {isUrgent && (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-status-red ring-1 ring-red-200">
                  <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                  Sin stock
                </span>
              )}
              {!isUrgent && isLow && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900 ring-1 ring-amber-200">
                  Comprar pronto
                </span>
              )}
            </div>
            {groupPets.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {groupPets.map((p) => (
                  <Link
                    key={p.id}
                    to={`/pets/${p.id}`}
                    className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 hover:bg-slate-200 transition-colors"
                  >
                    <PawPrint className="h-2.5 w-2.5" aria-hidden="true" />
                    {p.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <Button
            as={Link}
            to={`/groups/${group.id}/edit`}
            variant="ghost"
            size="sm"
            aria-label={`Editar ${group.name}`}
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="rounded-lg bg-white/60 px-2 py-1.5">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Stock total</p>
            <p className="text-sm font-semibold text-slate-900 mt-0.5">
              {formatGrams(projection.totalStockGr)}
            </p>
          </div>
          <div className="rounded-lg bg-white/60 px-2 py-1.5">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Consumo / día</p>
            <p className="text-sm font-semibold text-slate-900 mt-0.5">
              {projection.dailyConsumptionGr > 0
                ? formatGrams(projection.dailyConsumptionGr)
                : '—'}
            </p>
          </div>
          <div className="rounded-lg bg-white/60 px-2 py-1.5">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Días</p>
            <p
              className={`text-sm font-semibold mt-0.5 ${
                isUrgent ? 'text-status-red' : isLow ? 'text-amber-800' : 'text-slate-900'
              }`}
            >
              {isUnknown ? '—' : `~${Math.floor(days)}`}
            </p>
          </div>
        </div>

        <p className="text-[11px] text-slate-500 mt-2">
          {!isUnknown && (
            <>Umbral configurado: {projection.thresholdDays}d.</>
          )}
          {isUnknown && groupPets.length === 0 && (
            <>Sin mascotas asignadas todavía.</>
          )}
        </p>

        {/* Desglose del stock total para que el usuario sepa qué hay dónde */}
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
          {projection.dispenserStockGr > 0 && (
            <span className="inline-flex items-center gap-1">
              <Package className="h-3 w-3" aria-hidden="true" />
              Dispensers: {formatGrams(projection.dispenserStockGr)}
            </span>
          )}
          {projection.mainStockGr > 0 && (
            <span className="inline-flex items-center gap-1">
              <ShoppingBag className="h-3 w-3" aria-hidden="true" />
              Bolsa: {formatGrams(projection.mainStockGr)}
            </span>
          )}
          {projection.reserveStockGr > 0 && (
            <span className="inline-flex items-center gap-1">
              <ShoppingBag className="h-3 w-3" aria-hidden="true" />
              Reserva: {formatGrams(projection.reserveStockGr)}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
};

export default InventoryProjectionsPage;
